// Structured var-reference resolvers for object and array item access.
// Example: `var(--breakpoints[0].name)` -> `sm`.
import { isObjectVariableValue, parseObjectVariable } from '../../object-variable'
import { resolveObjectKeyValue } from './keys'
import { readAdjacentVarReferenceAt } from './adjacent-var'
import type { ClassTransformOptions, ParsedVarReference } from './types'

// Replace resolvable object/index var references inside plain text.
export function replaceStructuredVarReferencesInText(source: string, options: ClassTransformOptions): string {
  let output = ''
  // Scan text once and replace only resolvable structured var references.
  for (let index = 0; index < source.length; index += 1) {
    const token = readAdjacentVarReferenceAt(source, index)
    if (!token) {
      output += source[index]
      continue
    } else {
      // Continue replacement flow for parsed var reference.
    }

    const resolved = resolveStructuredVarReference(token.reference, options)
    output += resolved ?? token.raw
    index = token.reference.end - 1
  }

  // Return text with structured var references resolved.
  return output
}

// Resolve one structured var reference using object and array registries.
export function resolveStructuredVarReference(
  reference: ParsedVarReference,
  options: ClassTransformOptions,
): string | undefined {
  if (reference.arrayIndex !== null) {
    const arrayItem = options.arrayVariables[reference.variableName]?.[reference.arrayIndex]
    if (typeof arrayItem !== 'string') {
      return undefined
    } else if (!reference.objectKey) {
      return arrayItem
    } else {
      // Continue array-item object-key resolution.
    }

    return resolveArrayObjectItemValue(arrayItem, reference.objectKey, options)
  } else if (reference.objectKey) {
    const objectValue = options.objectVariables[reference.variableName]
    return objectValue ? resolveObjectKeyValue(objectValue, reference.objectKey) : undefined
  } else {
    // Return undefined for plain var references handled elsewhere.
    return undefined
  }
}

// Resolve one object-like array item key such as `{ name: sm }[name]`.
function resolveArrayObjectItemValue(item: string, key: string, options: ClassTransformOptions): string | undefined {
  if (!isObjectVariableValue(item)) {
    return undefined
  } else {
    // Continue array-item object parsing for keyed lookup.
  }

  const parsed = parseObjectVariable(item, options.objectVariables)
  // Return resolved object-key value from parsed array item.
  return resolveObjectKeyValue(parsed, key)
}
